import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Home as HomeIcon, NavigateNext } from '@mui/icons-material';

const PageHeader = ({ title, subtitle, backgroundImage, breadcrumbs = true }) => {
  const theme = useTheme();
  const location = useLocation();
  
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(path => path);
    const breadcrumbItems = paths.map((path, index) => {
      const url = `/${paths.slice(0, index + 1).join('/')}`;
      const isLast = index === paths.length - 1;
      return {
        label: path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' '),
        path: url,
        isLast,
      };
    });
    return [{ label: 'Home', path: '/', isLast: false }, ...breadcrumbItems];
  };

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'url(/assets/images/pattern.png)',
          backgroundRepeat: 'repeat',
          animation: 'slide 20s linear infinite',
          '@keyframes slide': {
            '0%': { transform: 'translateX(0) translateY(0)' },
            '100%': { transform: 'translateX(-100px) translateY(-100px)' },
          },
        }}
      />
      
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
          }}
        />
      )}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs && (
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
              sx={{ mb: 3, '& .MuiBreadcrumbs-ol': { justifyContent: 'center' } }}
            >
              {getBreadcrumbs().map((item, index) => (
                <Link
                  key={index}
                  component={RouterLink}
                  to={item.path}
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: item.isLast ? 'white' : 'rgba(255,255,255,0.7)',
                    fontSize: '0.875rem',
                    pointerEvents: item.isLast ? 'none' : 'auto',
                    '&:hover': { color: 'white' },
                  }}
                >
                  {item.label === 'Home' && <HomeIcon sx={{ fontSize: 16 }} />}
                  {item.label}
                </Link>
              ))}
            </Breadcrumbs>
          )}

          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: '2rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {title}
          </Typography>
          
          {subtitle && (
            <Typography
              variant="h6"
              align="center"
              sx={{
                opacity: 0.9,
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Decorative Elements */}
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 4 }}>
            <Chip
              label="Global Coverage"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label="24/7 Support"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label="Enterprise Grade"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Stack>
        </motion.div>
      </Container>

      {/* Wave Divider */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -2,
          left: 0,
          right: 0,
          height: 60,
          background: 'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="%23F8F9FA" fill-opacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/></svg>\')',
          backgroundSize: 'cover',
          zIndex: 2,
        }}
      />
    </Box>
  );
};

export default PageHeader;